/**
 * Create Or Edit Chart Tool
 * 
 * Create a new chart or edit an existing chart on a slide.
 * Uses categoryOperations and seriesOperations for data changes.
 */

import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import {
    CreateOrEditChartParamsSchema,
    type CreateOrEditChartParams,
} from "./createOrEditChart.schema";
import { BACKEND_BASE_URL } from "../../../../../services/communication/backendClient";
import { getOrCreatedocumentId } from "../../../../../services/documentIdManager";
import { applyShapeMetadata } from "../../../../../services/metadataService";

// ═══════════════════════════════════════════════════════════════════════════
// RESPONSE TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create or edit chart result
 */
export interface CreateOrEditChartResult {
    success: boolean;
    slideId: number;
    shapeId: number;
    operationType: "create" | "edit";
    chartType?: string;
    position?: {
        left: number;
        top: number;
        width: number;
        height: number;
    };
    warnings: string[];
    errors: string[];
    executionMethod?: "com";
}

/**
 * Backend styling format for chart operations (sent to Python COM backend)
 */
interface ChartBackendStyling {
    title?: {
        text?: string;
        font?: {
            name?: string;
            size?: number;
            bold?: boolean;
            color?: { fill: { r: number; g: number; b: number } };
        };
    };
    legend?: {
        visible?: boolean;
        position?: string;
        font?: {
            name?: string;
            size?: number;
            color?: { fill: { r: number; g: number; b: number } };
        };
    };
    axes?: Array<Record<string, unknown>>;
    background?: {
        color?: { fill?: { r: number; g: number; b: number } };
    };
    series?: Array<Record<string, unknown>>;
}

// ═══════════════════════════════════════════════════════════════════════════
// CREATE OR EDIT CHART TOOL
// ═══════════════════════════════════════════════════════════════════════════

// System prompt fallback for Keywords AI
// TODO: Revisit chart system prompt once we locate the other chart guidance prompt.
const CHART_SYSTEM_PROMPT = `### createOrEditChart
Create a new chart or edit an existing chart on a slide.

**When to use:**
- Creating data visualizations (bar, line, pie, etc.)
- Editing chart data or styling (bar colors, borders, axes, legend)
- Adding chart elements (title, legend, axes)
- **Copying ALL formatting from another chart (use inheritFrom with properties: ["all"])**
- When user says "format this chart like that chart" or "copy chart formatting"

**CRITICAL:** A "chart" is the graphic_frame object containing bars/lines/pies.
Separate text boxes, ovals, or shapes NEAR a chart are NOT part of the chart.
Use create_or_edit_shape for those.

**Chart types:** column, bar, line, pie, doughnut, area, scatter, combo, and more.

**Features:**
- Create 20+ chart types
- Category and series operations
- Axis configuration
- Legend and title styling
- Series styling (colors, markers)
- **Bulk inheritance: Copy ALL formatting from another chart**

**IMPORTANT:** For existing charts, get current data ranges with readSlide before modifying.

**Example - Copy formatting from another chart:**
\`\`\`json
{
  "slideId": 1,
  "operationType": "edit",
  "shapeId": 5,
  "chartConfig": {
    "inheritFrom": {
      "chartShapeId": 2,
      "properties": ["all"]
    }
  }
}
\`\`\`

**Example - Create chart:**
\`\`\`json
{
  "slideId": 1,
  "operationType": "create",
  "position": { "left": 50, "top": 100, "width": 400, "height": 300 },
  "chartConfig": {
    "chartType": "column",
    "data": {
      "categories": { "values": ["Q1", "Q2", "Q3", "Q4"] },
      "series": [
        { "name": "Revenue", "values": [100, 120, 140, 160] }
      ]
    }
  }
}
\`\`\``;

/**
 * Tool for creating or editing charts on slides.
 * 
 * Features:
 * - Create 20+ chart types
 * - Edit existing charts by ID
 * - Category operations (insert/remove)
 * - Series operations (insert/remove/replace/move)
 * - Axis configuration
 * - Legend configuration
 * - Series styling (fill, outline, markers, labels)
 * - Inherit from existing charts
 * - Metadata support in edit mode
 * 
 * Note: Charts don't have shadow. Fill and outline are optional.
 */
export class CreateOrEditChartTool extends ToolBase<CreateOrEditChartParams, CreateOrEditChartResult> {
    public readonly name = "createOrEditChart";
    public readonly description: ToolDescription;
    
    // System prompt description
    public readonly systemPromptDescription = CHART_SYSTEM_PROMPT;
    
    // Streaming operation fields
    fieldsToCheck = [
        { name: 'slideId', type: 'number' as const, list_status: false, order: 1 },
        { name: 'operationType', type: 'string' as const, list_status: false, order: 2 },
        { name: 'shapeId', type: 'number' as const, list_status: false, order: 3 },
    ];
    
    listsToCheck = [];

    constructor() {
        super();
        this.description = new ToolDescription(
            "createOrEditChart",
            `Create a new chart or edit an existing chart on a slide.

**PATTERN 0 (simple data edits):** For editing existing chart data/styling, call this tool DIRECTLY - do NOT call spacePlanning first!

**Purpose:** Create or modify charts with full data and styling control.

**CRITICAL - Chart vs Shape Distinction:**
- **Chart formatting** = The chart object itself (bars/lines/pies, their colors, borders, axes, legend, title, data labels)
- **NOT chart formatting** = Separate shapes near the chart (text boxes, ovals, rectangles, arrows)
- When user says "format the chart", they mean the chart object (graphic_frame type), NOT nearby decorative shapes
- Chart objects have shapeId and are of type "graphic_frame" in the slide structure

**Operation Types:**
- \`create\`: Create new chart (requires chartType, data)
- \`edit\`: Modify existing chart (requires shapeId)

**Chart Types (20+):**
column, column_stacked, column_stacked_100,
bar, bar_stacked, bar_stacked_100,
line, line_markers, line_stacked, line_stacked_100,
area, area_stacked, area_stacked_100,
pie, doughnut,
scatter, scatter_smooth, scatter_smooth_markers,
scatter_straight, scatter_straight_markers,
bubble, stock_ohlc, stock_vhlc,
radar, radar_filled, waterfall, funnel,
treemap, sunburst, histogram, box_whisker, combo

**Features:**
- Category operations: insert/remove
- Series operations: insert/remove/replace/move
- Axis configuration (bounds, units, labels, gridlines)
- Legend configuration
- Series styling (fill, outline, markers, data labels)
- **Bulk inheritance: Copy ALL formatting from another chart using inheritFrom**
- Metadata in edit mode

**Note:** Charts don't have shadow. Fill and outline are optional.

**Operations Processing Order:**
- Categories: remove first (original indices), then insert (post-removal)
- Series: remove → insert → replace → move

**Example (Create):**
\`\`\`json
{
  "slideId": 1,
  "operationType": "create",
  "position": { "left": 100, "top": 200, "width": 400, "height": 300 },
  "chartConfig": {
    "chartType": "column",
    "data": {
      "categories": { "values": ["Q1", "Q2", "Q3", "Q4"] },
      "series": [
        { "name": "Revenue", "values": [100, 120, 150, 180] },
        { "name": "Profit", "values": [20, 25, 35, 45] }
      ]
    },
    "legend": { "position": "bottom" }
  }
}
\`\`\`

**Example (Edit - Copy Formatting from Another Chart):**
\`\`\`json
{
  "slideId": 1,
  "operationType": "edit",
  "shapeId": 5,
  "chartConfig": {
    "inheritFrom": {
      "chartShapeId": 2,
      "properties": ["all"]
    }
  }
}
\`\`\`
This copies EVERYTHING (bar colors, borders, fonts, legend, axes, plot area, chart area, series styling) from chart shapeId=2.
**USE THIS when user says:** "format this chart like that chart", "copy chart formatting", "bottom chart should match top chart".
**IMPORTANT:** This copies the chart object formatting, NOT separate decorative shapes near the chart.
You can also specify specific properties: ["legend", "chartArea", "plotArea", "seriesStyles", "axes"]

**Example (Edit - Add Series):**
\`\`\`json
{
  "slideId": 1,
  "operationType": "edit",
  "shapeId": 5,
  "chartConfig": {
    "seriesOperations": [
      {
        "operation": "insert",
        "index": 2,
        "name": "2025 Forecast",
        "values": [200, 220, 250, 280],
        "inheritFormattingFrom": { "seriesIndex": 0, "properties": ["fill"] },
        "spacingConsequences": "Legend grows, may need chart resize"
      }
    ]
  }
}
\`\`\``,
            CreateOrEditChartParamsSchema
        );
    }

    public async execute(parameters: unknown): Promise<CreateOrEditChartResult> {
        const parseResult = safeParseToolParams(CreateOrEditChartParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(`Invalid parameters: ${parseResult.error}`);
        }

        const params = parseResult.data as CreateOrEditChartParams;
        const isCreate = params.operationType === "create";
        const { chartConfig } = params;
        
        console.log(`[CreateOrEditChartTool] ${isCreate ? 'Creating' : 'Editing'} chart on slide ${params.slideId}`);
        if (isCreate) {
            console.log(`  Chart type: ${chartConfig.chartType}`);
        } else {
            console.log(`  Shape ID: ${params.shapeId}`);
        }

        const warnings: string[] = [];
        const errors: string[] = [];

        try {
            if (isCreate) {
                // CREATE MODE: Create new chart
                const result = await this.createChart(params, chartConfig, warnings, errors);
                return await this.finalizeResult(params, result);
            } else {
                // EDIT MODE: Update existing chart
                const result = await this.editChart(params, chartConfig, warnings, errors);
                return await this.finalizeResult(params, result);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            errors.push(errorMessage);
            console.error(`[CreateOrEditChartTool] Error:`, error);
            
            return {
                success: false,
                slideId: params.slideId,
                shapeId: params.shapeId ?? 0,
                operationType: params.operationType,
                chartType: chartConfig.chartType,
                position: params.position ? {
                    left: params.position.left ?? 0,
                    top: params.position.top ?? 0,
                    width: params.position.width ?? 400,
                    height: params.position.height ?? 300,
                } : undefined,
                warnings,
                errors,
                executionMethod: "com",
            };
        }
    }

    private async finalizeResult(
        params: CreateOrEditChartParams,
        result: CreateOrEditChartResult
    ): Promise<CreateOrEditChartResult> {
        if (!result.success) {
            return result;
        }

        const shapeId = String(result.shapeId ?? params.shapeId ?? "");
        if (shapeId) {
            await applyShapeMetadata({
                slideId: params.slideId,
                shapeId,
                metadata: params.metadata,
            });
        }

        return result;
    }

    /**
     * Create a new chart
     */
    private async createChart(
        params: CreateOrEditChartParams,
        chartConfig: CreateOrEditChartParams["chartConfig"],
        warnings: string[],
        errors: string[]
    ): Promise<CreateOrEditChartResult> {
        if (!chartConfig.chartType || !chartConfig.data) {
            throw new Error("chartType and data are required for create mode");
        }

        // Transform data to backend format
        const backendData = {
            categories: chartConfig.data.categories?.values || [],
            series: chartConfig.data.series?.map(s => ({
                name: s.name,
                values: s.values || [],
                chart_type: s.chartType,
                axis_group: s.axisGroup,
            })) || []
        };

        // Transform styling to backend format
        const backendStyling = this.transformStyling(chartConfig);

        // Position in points (passed directly to backend, which also uses points)
        const backendPosition = params.position ? {
            left: params.position.left ?? 0,
            top: params.position.top ?? 0,
            width: params.position.width ?? 400,
            height: params.position.height ?? 300,
        } : undefined;

        // Get documentId for COM endpoint
        const documentId = await getOrCreatedocumentId();
        
        console.log(`[CreateOrEditChartTool] Calling COM backend /com/createOrEditChart`);
        console.log(`  Chart type: ${chartConfig.chartType}`);
        console.log(`  Categories: ${backendData.categories.length}`);
        console.log(`  Series: ${backendData.series.length}`);

        // Call COM backend API
        const response = await fetch(`${BACKEND_BASE_URL}/com/createOrEditChart`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                documentId: documentId,
                slideId: params.slideId,
                operationType: "create",
                chartType: chartConfig.chartType,
                data: backendData,
                position: backendPosition ? {
                    left: backendPosition.left,
                    top: backendPosition.top,
                    width: backendPosition.width,
                    height: backendPosition.height,
                } : undefined,
                styling: backendStyling,
                // Chart-type specific options
                bar_column_options: chartConfig.barColumnOptions,
                pie_options: chartConfig.pieOptions,
                line_options: chartConfig.lineOptions,
                // Chart area styling
                chart_area: chartConfig.chartArea,
                // Axes configuration
                axes: chartConfig.axes,
                // Plot area styling (data region)
                plot_area: chartConfig.plotArea,
                // Series styling (with axisGroup, chartType for combos)
                series_styles: chartConfig.seriesStyles,
                // Bulk inheritance from another chart
                inherit_from: chartConfig.inheritFrom,
            }),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Backend error (${response.status}): ${errorText}`);
        }

        const result = await response.json();
        console.log(`[CreateOrEditChartTool] Backend response:`, result);

        if (result.success) {
            // COM endpoint returns shapeId directly, position in result.position
            const shapeId = result.shapeId || result.data?.shapeId;
            const position = result.position || result.data?.position;
            
            // Position returned in points (no conversion needed)
            const positionInPoints = position && position.left !== undefined && position.top !== undefined && position.width !== undefined && position.height !== undefined ? {
                left: position.left,
                top: position.top,
                width: position.width,
                height: position.height,
            } : params.position;

            return {
                success: true,
                slideId: params.slideId,
                shapeId: shapeId || 0,
                operationType: "create",
                chartType: result.chartType || chartConfig.chartType,
                position: positionInPoints ? {
                    left: positionInPoints.left ?? 0,
                    top: positionInPoints.top ?? 0,
                    width: positionInPoints.width ?? 400,
                    height: positionInPoints.height ?? 300,
                } : undefined,
                warnings: [...warnings, ...(result.warnings || [])],
                errors: [...errors, ...(result.errors || [])],
                executionMethod: "com",
            };
        } else {
            errors.push(result.message || result.error || 'Chart creation failed');
            return {
                success: false,
                slideId: params.slideId,
                shapeId: 0,
                operationType: "create",
                chartType: chartConfig.chartType,
                position: params.position ? {
                    left: params.position.left ?? 0,
                    top: params.position.top ?? 0,
                    width: params.position.width ?? 400,
                    height: params.position.height ?? 300,
                } : undefined,
                warnings: [...warnings, ...(result.warnings || [])],
                errors,
                executionMethod: "com",
            };
        }
    }

    /**
     * Edit an existing chart
     */
    private async editChart(
        params: CreateOrEditChartParams,
        chartConfig: CreateOrEditChartParams["chartConfig"],
        warnings: string[],
        errors: string[]
    ): Promise<CreateOrEditChartResult> {
        if (!params.shapeId) {
            throw new Error("shapeId is required for edit mode");
        }

        // First, read the existing chart to get current data
        // Note: For now, we'll skip reading and just update with the provided data
        // TODO: Add read_chart COM endpoint if needed for full edit support
        console.log(`[CreateOrEditChartTool] Editing chart (shapeId: ${params.shapeId})`);
        console.log(`[CreateOrEditChartTool] Note: Using provided data directly (read_chart not yet implemented in COM)`);

        // Get current data structure from the edit request
        // In a full implementation, we'd start with existingChart.data
        let categories = chartConfig.data?.categories?.values || [];
        let series = chartConfig.data?.series || [];

        // Apply category operations (remove first, then insert)
        if (chartConfig.categoryOperations && chartConfig.categoryOperations.length > 0) {
            console.log(`[CreateOrEditChartTool] Applying ${chartConfig.categoryOperations.length} category operations`);
            
            // Separate remove and insert operations
            const removeOps = chartConfig.categoryOperations.filter(op => op.operation === "remove");
            const insertOps = chartConfig.categoryOperations.filter(op => op.operation === "insert");

            // Apply removes first (in reverse order to maintain indices)
            const sortedRemoves = [...removeOps].sort((a, b) => b.index - a.index);
            for (const op of sortedRemoves) {
                if (op.index >= 0 && op.index < categories.length) {
                    categories.splice(op.index, 1);
                    // Also remove corresponding values from all series
                    series.forEach(s => {
                        if (s.values && op.index < s.values.length) {
                            s.values.splice(op.index, 1);
                        }
                    });
                }
            }

            // Apply inserts (in order, using post-removal indices)
            const sortedInserts = [...insertOps].sort((a, b) => a.index - b.index);
            for (const op of sortedInserts) {
                if (op.value !== undefined) {
                    const insertIndex = Math.min(op.index, categories.length);
                    categories.splice(insertIndex, 0, op.value);
                    // Add null values to all series (user should provide values via series operations)
                    series.forEach(s => {
                        if (s.values) {
                            s.values.splice(insertIndex, 0, null);
                        }
                    });
                }
            }
        }

        // Apply series operations (remove → insert → replace → move)
        if (chartConfig.seriesOperations && chartConfig.seriesOperations.length > 0) {
            console.log(`[CreateOrEditChartTool] Applying ${chartConfig.seriesOperations.length} series operations`);
            
            // Group operations by type
            const removeOps = chartConfig.seriesOperations.filter(op => op.operation === "remove");
            const insertOps = chartConfig.seriesOperations.filter(op => op.operation === "insert");
            const replaceOps = chartConfig.seriesOperations.filter(op => op.operation === "replace");
            const moveOps = chartConfig.seriesOperations.filter(op => op.operation === "move");

            // 1. Remove operations (in reverse order)
            const sortedRemoves = [...removeOps].sort((a, b) => (b.index ?? 0) - (a.index ?? 0));
            for (const op of sortedRemoves) {
                if (op.index !== undefined && op.index >= 0 && op.index < series.length) {
                    series.splice(op.index, 1);
                }
            }

            // 2. Insert operations
            const sortedInserts = [...insertOps].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
            for (const op of sortedInserts) {
                if (op.name && op.values) {
                    const insertIndex = op.index !== undefined ? Math.min(op.index, series.length) : series.length;
                    const newSeries: any = {
                        name: op.name,
                        values: op.values,
                    };
                    
                    // Handle inheritFormattingFrom
                    if (op.inheritFormattingFrom) {
                        const sourceSeries = series[op.inheritFormattingFrom.seriesIndex];
                        if (sourceSeries) {
                            const props = op.inheritFormattingFrom.properties || ["all"];
                            if (props.includes("all") || props.includes("fill")) {
                                newSeries.fill = sourceSeries.fill;
                            }
                            if (props.includes("all") || props.includes("outline")) {
                                newSeries.outline = sourceSeries.outline;
                            }
                            // Add other properties as needed
                        }
                    }
                    
                    series.splice(insertIndex, 0, newSeries);
                }
            }

            // 3. Replace operations
            for (const op of replaceOps) {
                if (op.index !== undefined && op.index >= 0 && op.index < series.length && op.name && op.values) {
                    series[op.index] = {
                        ...series[op.index],
                        name: op.name,
                        values: op.values,
                    };
                }
            }

            // 4. Move operations
            for (const op of moveOps) {
                if (op.fromIndex !== undefined && op.toIndex !== undefined &&
                    op.fromIndex >= 0 && op.fromIndex < series.length &&
                    op.toIndex >= 0 && op.toIndex < series.length) {
                    const [movedSeries] = series.splice(op.fromIndex, 1);
                    series.splice(op.toIndex, 0, movedSeries);
                }
            }
        }

        // Transform updated data to backend format
        const backendData = {
            categories: categories,
            series: series.map((s: any) => ({
                name: s.name,
                values: s.values || [],
                chart_type: s.chart_type || s.chartType,
                axis_group: s.axis_group || s.axisGroup,
            })),
        };

        // Transform styling
        const backendStyling = this.transformStyling(chartConfig);

        // Position in points (passed directly to backend, which also uses points)
        const backendPosition = params.position ? {
            left: params.position.left ?? 0,
            top: params.position.top ?? 0,
            width: params.position.width ?? 400,
            height: params.position.height ?? 300,
        } : undefined;

        // Get documentId for COM endpoint
        const documentId = await getOrCreatedocumentId();
        
        console.log(`[CreateOrEditChartTool] Calling COM backend /com/createOrEditChart (edit)`);
        console.log(`  Updated categories: ${backendData.categories.length}`);
        console.log(`  Updated series: ${backendData.series.length}`);

        // Call COM backend API to update chart
        const updateResponse = await fetch(`${BACKEND_BASE_URL}/com/createOrEditChart`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                documentId: documentId,
                slideId: params.slideId,
                operationType: "edit",
                shapeId: params.shapeId,
                chartType: chartConfig.chartType, // Allow changing chart type
                data: backendData,
                styling: backendStyling,
                position: backendPosition ? {
                    left: backendPosition.left,
                    top: backendPosition.top,
                    width: backendPosition.width,
                    height: backendPosition.height,
                } : undefined,
                // Chart-type specific options
                bar_column_options: chartConfig.barColumnOptions,
                pie_options: chartConfig.pieOptions,
                line_options: chartConfig.lineOptions,
                // Chart area styling
                chart_area: chartConfig.chartArea,
                // Axes configuration
                axes: chartConfig.axes,
                // Plot area styling (data region)
                plot_area: chartConfig.plotArea,
                // Series styling (with axisGroup, chartType for combos)
                series_styles: chartConfig.seriesStyles,
                // Data operations (let backend handle them directly)
                category_operations: chartConfig.categoryOperations,
                series_operations: chartConfig.seriesOperations,
                // Bulk inheritance from another chart
                inherit_from: chartConfig.inheritFrom,
            }),
        });

        if (!updateResponse.ok) {
            const errorText = await updateResponse.text();
            throw new Error(`Backend error (${updateResponse.status}): ${errorText}`);
        }

        const updateResult = await updateResponse.json();
        console.log(`[CreateOrEditChartTool] Backend update response:`, updateResult);

        if (updateResult.success) {
            // Position returned in points (no conversion needed)
            const position = updateResult.position || updateResult.data?.position;
            const positionInPoints = position && position.left !== undefined && position.top !== undefined && position.width !== undefined && position.height !== undefined ? {
                left: position.left,
                top: position.top,
                width: position.width,
                height: position.height,
            } : params.position;

            return {
                success: true,
                slideId: params.slideId,
                shapeId: params.shapeId,
                operationType: "edit",
                chartType: updateResult.chartType || chartConfig.chartType,
                position: positionInPoints ? {
                    left: positionInPoints.left ?? 0,
                    top: positionInPoints.top ?? 0,
                    width: positionInPoints.width ?? 400,
                    height: positionInPoints.height ?? 300,
                } : undefined,
                warnings: [...warnings, ...(updateResult.warnings || [])],
                errors: [...errors, ...(updateResult.errors || [])],
                executionMethod: "com",
            };
        } else {
            errors.push(updateResult.message || updateResult.error || 'Chart update failed');
            return {
                success: false,
                slideId: params.slideId,
                shapeId: params.shapeId,
                operationType: "edit",
                chartType: chartConfig.chartType,
                position: params.position ? {
                    left: params.position.left ?? 0,
                    top: params.position.top ?? 0,
                    width: params.position.width ?? 400,
                    height: params.position.height ?? 300,
                } : undefined,
                warnings: [...warnings, ...(updateResult.warnings || [])],
                errors,
                executionMethod: "com",
            };
        }
    }

    /**
     * Transform styling from schema format to backend format
     */
    private transformStyling(
        chartConfig: CreateOrEditChartParams["chartConfig"]
    ): ChartBackendStyling {
        const styling: ChartBackendStyling = {};

        // Title
        if (chartConfig.title && !('inheritFrom' in chartConfig.title)) {
            styling.title = {
                text: chartConfig.title.text,
            };
            if (chartConfig.title.textFormat && !('inheritFrom' in chartConfig.title.textFormat)) {
                styling.title.font = {
                    name: chartConfig.title.textFormat.fontFamily,
                    size: chartConfig.title.textFormat.fontSize,
                    bold: chartConfig.title.textFormat.bold,
                };
                if (chartConfig.title.textFormat.color) {
                    styling.title.font.color = {
                        fill: {
                            r: parseInt(chartConfig.title.textFormat.color.slice(1, 3), 16),
                            g: parseInt(chartConfig.title.textFormat.color.slice(3, 5), 16),
                            b: parseInt(chartConfig.title.textFormat.color.slice(5, 7), 16),
                        },
                    };
                }
            }
        }

        // Legend
        if (chartConfig.legend && !('inheritFrom' in chartConfig.legend)) {
            styling.legend = {
                visible: chartConfig.legend.position !== "none",
                position: chartConfig.legend.position === "none" ? undefined : chartConfig.legend.position,
            };
            if (chartConfig.legend.textFormat && !('inheritFrom' in chartConfig.legend.textFormat)) {
                styling.legend.font = {
                    name: chartConfig.legend.textFormat.fontFamily,
                    size: chartConfig.legend.textFormat.fontSize,
                };
                if (chartConfig.legend.textFormat.color) {
                    styling.legend.font.color = {
                        fill: {
                            r: parseInt(chartConfig.legend.textFormat.color.slice(1, 3), 16),
                            g: parseInt(chartConfig.legend.textFormat.color.slice(3, 5), 16),
                            b: parseInt(chartConfig.legend.textFormat.color.slice(5, 7), 16),
                        },
                    };
                }
            }
        }

        // Axes
        if (chartConfig.axes) {
            styling.axes = [];
            
            if (chartConfig.axes.categoryAxis) {
                styling.axes.push(this.transformAxis(chartConfig.axes.categoryAxis, "labels"));
            }
            if (chartConfig.axes.primaryValueAxis) {
                styling.axes.push(this.transformAxis(chartConfig.axes.primaryValueAxis, "primary"));
            }
            if (chartConfig.axes.secondaryValueAxis) {
                styling.axes.push(this.transformAxis(chartConfig.axes.secondaryValueAxis, "secondary"));
            }
        }

        // Background/Plot Area
        if (chartConfig.plotArea && !('inheritFrom' in chartConfig.plotArea)) {
            styling.background = {};
            if (chartConfig.plotArea.fill && !('inheritFrom' in chartConfig.plotArea.fill)) {
                styling.background.color = {
                    fill: chartConfig.plotArea.fill.color ? {
                        r: parseInt(chartConfig.plotArea.fill.color.slice(1, 3), 16),
                        g: parseInt(chartConfig.plotArea.fill.color.slice(3, 5), 16),
                        b: parseInt(chartConfig.plotArea.fill.color.slice(5, 7), 16),
                    } : undefined,
                };
            }
        }

        // Note: Series styling (fill, outline, marker, dataLabels) is passed directly
        // to backend via seriesStyles, not transformed here

        return Object.keys(styling).length > 0 ? styling : undefined;
    }

    /**
     * Transform axis configuration to backend format
     */
    private transformAxis(axis: any, axisType: "labels" | "primary" | "secondary"): any {
        const backendAxis: any = {
            type: axisType,
        };

        if (axis.bounds) {
            if (axis.bounds.minimum !== undefined) {
                backendAxis.MinimumScale = axis.bounds.minimum;
            }
            if (axis.bounds.maximum !== undefined) {
                backendAxis.MaximumScale = axis.bounds.maximum;
            }
        }

        if (axis.units) {
            if (axis.units.major !== undefined) {
                backendAxis.MajorIncrement = axis.units.major;
            }
            if (axis.units.minor !== undefined) {
                backendAxis.MinorIncrement = axis.units.minor;
            }
        }

        if (axis.title) {
            backendAxis.title = axis.title.text;
        }

        if (axis.gridlines) {
            backendAxis.gridlines = {
                HasMajorGridlines: axis.gridlines.major?.show ?? false,
                HasMinorGridlines: axis.gridlines.minor?.show ?? false,
            };
        }

        if (axis.textFormat) {
            backendAxis.font = {
                name: axis.textFormat.fontFamily,
                size: axis.textFormat.fontSize,
            };
            if (axis.textFormat.color) {
                backendAxis.font.color = {
                    fill: {
                        r: parseInt(axis.textFormat.color.slice(1, 3), 16),
                        g: parseInt(axis.textFormat.color.slice(3, 5), 16),
                        b: parseInt(axis.textFormat.color.slice(5, 7), 16),
                    },
                };
            }
        }

        return backendAxis;
    }
}


